import { Appearance, BODY_COLORS, BODY_TYPES, HAIR_COLORS, HAIR_STYLES, OUTFITS, SKIN_TONES, type Appearance as AppearanceT } from "@townsy/contracts";

/**
 * Maps the public appearance fields of a realtime `ActorState` (plain strings in the Colyseus
 * schema) to a validated contract `Appearance` for the renderer. Older servers do not send
 * `bodyType` / `hairColor` (empty or missing): the contract defaults apply. A single unknown value
 * falls back field-by-field so one bad value never repaints the whole avatar.
 */
export interface AppearanceStateLike {
  bodyType?: string;
  bodyColor: string;
  skinTone: string;
  hairStyle: string;
  hairColor?: string;
  outfit: string;
}

export const FALLBACK_APPEARANCE: AppearanceT = {
  bodyType: "male",
  bodyColor: "slate",
  skinTone: "s3",
  hairStyle: "short",
  hairColor: "brown",
  outfit: "casual",
  version: 1,
};

/** Cache key covering every presentation field (a change in any of them re-maps the appearance). */
export function appearanceKey(a: AppearanceStateLike): string {
  return `${a.bodyType ?? ""}|${a.bodyColor}|${a.skinTone}|${a.hairStyle}|${a.hairColor ?? ""}|${a.outfit}`;
}

function pick<T extends string>(allowed: readonly T[], value: string | undefined, fallback: T): T {
  return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

export function appearanceFromState(a: AppearanceStateLike): AppearanceT {
  const parsed = Appearance.safeParse({
    bodyType: a.bodyType || undefined,
    bodyColor: a.bodyColor,
    skinTone: a.skinTone,
    hairStyle: a.hairStyle,
    hairColor: a.hairColor || undefined,
    outfit: a.outfit,
    version: 1,
  });
  if (parsed.success) return parsed.data;
  const f = FALLBACK_APPEARANCE;
  return {
    bodyType: pick(BODY_TYPES, a.bodyType, f.bodyType),
    bodyColor: pick(BODY_COLORS, a.bodyColor, f.bodyColor),
    skinTone: pick(SKIN_TONES, a.skinTone, f.skinTone),
    hairStyle: pick(HAIR_STYLES, a.hairStyle, f.hairStyle),
    hairColor: pick(HAIR_COLORS, a.hairColor, f.hairColor),
    outfit: pick(OUTFITS, a.outfit, f.outfit),
    version: 1,
  };
}
